import {
  useState,
  useCallback,
  ComponentProps,
  useEffect,
  useRef,
} from "react";
import CloseIcon from "@/assets/icons/close.svg?react";
import DonCircleLinear from "@/assets/icons/don-filled-linear.svg?react";
import { cn } from "@/lib/utils";
import { openLink } from "@telegram-apps/sdk-react";
import { Notification as NotificationType } from "./types";
import {
  NotificationProvider,
  useNotificationContext,
} from "./NotificationProvider";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { getImageByPriority } from "@/shared/utils/common";

interface PushNotificationCardProps {
  notification: NotificationType;
  onDismiss: (id: string) => void;
  index: number;
  onClick?: () => void;
}

const SWIPE_THRESHOLD = 50;
const ANIMATION_DURATION = 300;

const NotificationLoading = (props: ComponentProps<"div">) => {
  return (
    <div {...props}>
      <div className="skeleton w-full h-[66px] rounded-xl" />
    </div>
  );
};

const PushNotificationCard = ({
  notification,
  onDismiss,
  onClick,
}: PushNotificationCardProps) => {
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [touchDeltaX, setTouchDeltaX] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  const {
    id,
    notification: { title, body, image },
  } = notification;

  const handleDismiss = useCallback(
    (direction: "left" | "right" | "button") => {
      const screenWidth = window.innerWidth;
      const translateX = direction === "left" ? -screenWidth : screenWidth;
      setTouchDeltaX(translateX);

      setTimeout(() => onDismiss(id), ANIMATION_DURATION);
    },
    [id, onDismiss]
  );

  const handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    setTouchStartX(e.touches[0].clientX);
    setTouchDeltaX(0);
    setIsSwiping(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartX === null || !isSwiping) return;

    e.preventDefault();
    const deltaX = e.touches[0].clientX - touchStartX;

    // Only allow horizontal movement
    setTouchDeltaX(deltaX);
  };

  const handleTouchEnd = () => {
    if (touchStartX === null || !isSwiping) return;

    const absDelta = Math.abs(touchDeltaX);
    if (absDelta > SWIPE_THRESHOLD) {
      handleDismiss(touchDeltaX > 0 ? "right" : "left");
    } else {
      setTouchDeltaX(0);
    }
    setTouchStartX(null);
    setIsSwiping(false);
  };

  // Add passive: false for better control on iOS
  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const options = { passive: false };
    card.addEventListener("touchmove", handleTouchMove as any, options);

    return () => {
      card.removeEventListener("touchmove", handleTouchMove as any);
    };
  }, [touchStartX, isSwiping]);

  return (
    <div
      ref={cardRef}
      className="bg-[#333338] p-3 w-full rounded-xl transition-transform duration-300 ease-out"
      style={{
        transform: `translateX(${touchDeltaX}px)`,
        touchAction: "pan-y", // Allow vertical scrolling but prevent horizontal browser gestures
        backdropFilter: "blur(16px)",
        borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
      }}
      onClick={onClick}
      onTouchStart={handleTouchStart}
      onTouchMove={isSwiping ? handleTouchMove : undefined}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
    >
      <div className={cn("flex items-start justify-between")}>
        <div className="flex items-center gap-[10px] flex-1 min-w-0">
          <div className="size-9 bg-black flex-center rounded-md border border-white/10 shrink-0">
            {image ? (
              <img
                src={getImageByPriority(image, "small")}
                alt="Notification"
                className="w-full h-full rounded-md object-cover"
              />
            ) : (
              <DonCircleLinear />
            )}
          </div>
          <div className="flex flex-col gap-0.5 min-w-0">
            <h2 className="text-base text-white font-semibold truncate">
              {title}
            </h2>
            <p className="text-xs text-gray-1 leading-130 line-clamp-2">
              {body}
            </p>
          </div>
        </div>
        <CloseIcon
          className="text-gray-1 cursor-pointer shrink-0"
          onClick={() => handleDismiss("right")}
        />
      </div>
    </div>
  );
};

const NotificationDisplay = () => {
  const { notificationQueue, dismissNotification, error, isFetchingNextPage } =
    useNotificationContext();
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    if (error && error.message) {
      // showCustomToast(t("notification_error"), "error");
    }
  }, [error, t]);

  const notificationClickHandler = (link: string | null) => {
    if (!link) return;

    if (link.startsWith("http")) {
      openLink(link);
    }

    navigate(link);
  };

  if (!notificationQueue.length) {
    return null;
  }

  return (
    <div className="overflow-hidden">
      <div className="container mt-4">
        {notificationQueue.length > 0 && (
          <div
            className={cn("relative h-[70px] transition-all", {
              "h-[78px]": notificationQueue.length > 1,
            })}
          >
            {notificationQueue.map((notification, index) => (
              <div
                key={notification.id}
                className="absolute w-full transition-all duration-300 ease-out"
                style={{
                  transform: `translateY(${index * 12}px) scale(${
                    1 - index * 0.05
                  })`,
                  zIndex: notificationQueue.length - index,
                  pointerEvents: index === 0 ? "auto" : "none",
                }}
              >
                <PushNotificationCard
                  notification={notification}
                  onDismiss={dismissNotification}
                  index={index}
                  onClick={() =>
                    notificationClickHandler(notification.notification.link)
                  }
                />
              </div>
            ))}
          </div>
        )}

        {/* Show skeleton at the bottom when fetching next page */}
        {isFetchingNextPage && !notificationQueue.length && (
          <div
            className="w-full transition-all duration-300 ease-out"
            style={{
              transform: `translateY(${
                notificationQueue.length * 12
              }px) scale(${1 - notificationQueue.length * 0.05})`,
              zIndex: 0,
              opacity: 0.5,
            }}
          >
            <NotificationLoading />
          </div>
        )}
      </div>
    </div>
  );
};

const PushNotification = () => {
  return (
    <NotificationProvider>
      <NotificationDisplay />
    </NotificationProvider>
  );
};

export default PushNotification;
